/**
 * @file InvitationsTable.tsx
 * @description Вкладка «Инвайты в ЛК» страницы «Уведомления» (phase19):
 * сводка (всего / активировано / ожидает) + таблица инвайтов со статусами
 * и фильтром. «Активирован» = клиент создал пароль по этой ссылке.
 */

import { useEffect, useState } from "react";

import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import { EditorialCard } from "../../../../components/ui/EditorialCard";
import {
  INVITATION_SOURCE_LABELS,
  INVITATION_STATUS_LABELS,
  listCustomerInvitations,
  type InvitationItem,
  type InvitationStatus,
} from "../../../../lib/api-notification-log";
import { formatLogDate, TabEmpty, TabStateBlock, tabErrorMessage } from "../NotificationLogPage";

const PAGE_SIZE = 25;

const STATUS_TONE: Record<InvitationStatus, "primary" | "secondary" | "outline"> = {
  activated: "primary",
  pending: "secondary",
  expired: "outline",
  superseded: "outline",
};

export function InvitationsTable() {
  const [items, setItems] = useState<InvitationItem[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<{ total: number; activated: number; pending: number } | null>(null);
  const [status, setStatus] = useState<InvitationStatus | undefined>();
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    listCustomerInvitations({ status, page, limit: PAGE_SIZE })
      .then((res) => {
        setItems(res.items);
        setTotal(res.total);
        setSummary(res.summary);
      })
      .catch((err) => setError(tabErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [status, page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const conversion =
    summary && summary.total > 0
      ? Math.round((summary.activated / summary.total) * 100)
      : null;

  return (
    <div className="flex flex-col gap-4">
      {summary && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <SummaryCard label="Отправлено инвайтов" value={String(summary.total)} />
          <SummaryCard
            label="Активировано (сработали)"
            value={`${summary.activated}${conversion !== null ? ` · ${conversion}%` : ""}`}
          />
          <SummaryCard label="Ожидают активации" value={String(summary.pending)} />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <span className="mr-1 text-sm text-on-surface-variant">Статус:</span>
        {([undefined, "pending", "activated", "expired", "superseded"] as const).map((s) => (
          <Button
            key={s ?? "all"}
            variant={status === s ? "default" : "outline"}
            size="sm"
            onClick={() => {
              setStatus(s);
              setPage(1);
            }}
          >
            {s ? INVITATION_STATUS_LABELS[s] : "Все"}
          </Button>
        ))}
      </div>

      <TabStateBlock loading={loading} error={error} />
      {!loading && !error && items.length === 0 && (
        <TabEmpty text="Инвайтов нет — отправьте клиенту приглашение в ЛК из его карточки." />
      )}

      {!loading && !error && items.length > 0 && (
        <EditorialCard>
          <EditorialCard.Body className="!p-0 overflow-x-auto">
            <table className="w-full min-w-[700px] text-sm">
              <thead className="bg-on-surface/5 text-left">
                <tr>
                  <th className="px-4 py-3 font-semibold">Отправлен</th>
                  <th className="px-4 py-3 font-semibold">Клиент</th>
                  <th className="px-4 py-3 font-semibold">Источник</th>
                  <th className="px-4 py-3 font-semibold">Действует до</th>
                  <th className="px-4 py-3 font-semibold">Статус</th>
                  <th className="px-4 py-3 font-semibold">Кем создан</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-t border-on-surface/8">
                    <td className="whitespace-nowrap px-4 py-2.5 text-on-surface-variant">
                      {formatLogDate(it.createdAt)}
                    </td>
                    <td className="px-4 py-2.5">
                      {it.customer.displayName ?? "Без имени"}
                      {it.customer.accountActivated ? (
                        <div className="text-xs text-on-surface-variant">ЛК активен</div>
                      ) : null}
                    </td>
                    <td className="px-4 py-2.5">{INVITATION_SOURCE_LABELS[it.source]}</td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-on-surface-variant">
                      {formatLogDate(it.expiresAt)}
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge variant={STATUS_TONE[it.status]}>
                        {INVITATION_STATUS_LABELS[it.status]}
                      </Badge>
                      {it.consumedAt ? (
                        <div className="mt-0.5 text-xs text-on-surface-variant">
                          {formatLogDate(it.consumedAt)}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-2.5">
                      {it.createdBy?.displayName ?? "Система"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </EditorialCard.Body>
        </EditorialCard>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Назад
          </Button>
          <span className="text-on-surface-variant">
            {page} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Вперёд
          </Button>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <EditorialCard>
      <EditorialCard.Body className="!py-4">
        <div className="label-editorial">{label}</div>
        <div className="mt-1 text-2xl font-semibold tracking-tight">{value}</div>
      </EditorialCard.Body>
    </EditorialCard>
  );
}
